/**
 * The Inspector's history: when it looked, why, how long it took, and what
 * each look raised. Every row opens the run's own page.
 *
 * @module routes/$org.compliance.inspector
 */
import { Loupe } from "@lucasmarkes/hairline/react";
import { InspectorIcon } from "../components/InspectorIcon";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  CircleAlertIcon,
  CalendarClockIcon,
  CalendarPlusIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
  ArrowUpDownIcon,
  ListFilterIcon,
  PlayIcon,
  XIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useInspections } from "../state/inspections";
import { Alert, AlertAction, AlertDescription, AlertTitle } from "../components/ui/alert";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Popover, PopoverClose, PopoverPopup, PopoverTrigger } from "../components/ui/popover";
import { Card, CardPanel } from "../components/ui/card";
import { CardSpotlight } from "../components/ui/card-spotlight";
import { Counter } from "../components/ui/counter";
import { PixelField } from "../components/ui/pixel-field";
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "../components/ui/empty";
import { HairlineArt } from "../components/ui/hairline-art";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { InspectionRunRow } from "../components/regulation/inspector/InspectionRunRow";
import {
  activeFilterCount,
  countdownParts,
  filterInspections,
  inspectionKind,
  INSPECTION_KINDS,
  INSPECTION_SORTS,
  INSPECTION_STATUSES,
  KIND_LABEL,
  NO_FILTERS,
  paginate,
  SORT_LABEL,
  sortInspections,
  STATUS_PILL,
  toggleKind,
  toggleStatus,
  type InspectionFilters,
  type InspectionSort,
} from "../components/regulation/inspector/inspectorView";

import {
  ComplianceOrganizationMissing,
  ComplianceUnavailable,
} from "../components/regulation/ComplianceUnavailable";
import { ListSkeleton } from "../components/regulation/LoadingSkeletons";
import { RegulationPageShell } from "../components/regulation/RegulationPageShell";
import { InspectionActivityCard } from "../components/regulation/inspector/InspectionActivity";
import {
  defaultScheduleAt,
  toDateTimeLocal,
  type InspectionRun,
} from "../components/regulation/inspector/inspectorView";
import { useRegulationOrg } from "../components/regulation/useRegulationOrg";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../components/ui/dialog";
import { Group, GroupSeparator } from "../components/ui/group";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "../components/ui/select";
import {
  describeInspectionSchedule,
  nextInspectionRun,
  WEEKDAY_NAMES,
  type InspectionSchedule,
} from "@repo/shared/inspectionSchedule";
import {
  Menu,
  MenuItem,
  MenuPopup,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuTrigger,
} from "../components/ui/menu";
import { resolvePrimaryEnvironmentHttpUrl } from "../environments/primary/target";
import { useOrgProfile } from "../state/orgProfile";

const when = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

function RegulationInspectorRoute() {
  const { org } = Route.useParams();
  return <InspectionHistory key={org} />;
}
function InspectionHistory() {
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const { org: orgSegment } = Route.useParams();
  const { org, isLoading } = useRegulationOrg();
  const organizationId = org?.id ?? null;
  const { canManage, profile, save: saveProfile } = useOrgProfile(organizationId);
  const read = useInspections(organizationId);
  const runs = read.snapshot?.runs ?? null;
  const scheduled = read.snapshot?.scheduled ?? null;
  const load = read.refresh;
  const navigate = Route.useNavigate();
  const [actionError, setError] = useState<string | null>(null);
  const error = actionError ?? read.error;
  const [starting, setStarting] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleAt, setScheduleAt] = useState("");
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [booking, setBooking] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  // The standing schedule: edited in a dialog, saved to the organisation's
  // profile; the strip below the title says what it is and when it fires next.
  const [scheduleEditorOpen, setScheduleEditorOpen] = useState(false);
  const [scheduleDraft, setScheduleDraft] = useState<InspectionSchedule | null>(null);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [scheduleSaveError, setScheduleSaveError] = useState<string | null>(null);
  // Each time the editor opens is a distinct session; a save that finishes
  // after the dialog was closed and reopened must not close the new one.
  const scheduleSession = useRef(0);
  const [filters, setFilters] = useState<InspectionFilters>(NO_FILTERS);
  const [sort, setSort] = useState<InspectionSort>("newest");
  const [query, setQuery] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  // A new filter, sort, search or page size starts from the first page.
  useEffect(() => setPage(1), [filters, sort, query, pageSize]);
  // The countdown ticks once a minute; everything else derived from `now`
  // (the next scheduled sweep) rides along.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const runNow = async () => {
    if (!organizationId || starting) return;
    setStarting(true);
    setError(null);
    try {
      const response = await fetch(
        resolvePrimaryEnvironmentHttpUrl("/api/amoreg/inspector/runs", { organizationId }),
        {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ trigger: "manual" }),
        },
      );
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as { error?: string } | null;
        setError(body?.error ?? `Could not start an inspection (${response.status}).`);
        return;
      }
      const body: unknown = await response.json();
      if (!mounted.current) return;
      load();
      if (
        body &&
        typeof body === "object" &&
        "threadId" in body &&
        typeof body.threadId === "string" &&
        body.threadId.length > 0
      )
        await navigate({
          to: "/$org/compliance/inspector/$threadId",
          params: { org: orgSegment, threadId: body.threadId },
        });
      else throw new Error("Missing run identity");
    } catch {
      setError("The run could not be confirmed. Refresh the history before starting another.");
      load();
    } finally {
      setStarting(false);
    }
  };

  const scheduleRun = async () => {
    if (!organizationId || booking) return;
    const at = new Date(scheduleAt);
    if (Number.isNaN(at.getTime())) {
      setScheduleError("Pick a date and time.");
      return;
    }
    setBooking(true);
    setScheduleError(null);
    try {
      const response = await fetch(
        resolvePrimaryEnvironmentHttpUrl("/api/amoreg/inspector/schedule", { organizationId }),
        {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ at: at.toISOString() }),
        },
      );
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as { error?: string } | null;
        setScheduleError(body?.error ?? `Could not schedule the run (${response.status}).`);
        return;
      }
      setScheduleOpen(false);
      await load();
    } catch {
      setScheduleError("The schedule could not be confirmed. Try again.");
    } finally {
      setBooking(false);
    }
  };

  const cancelScheduled = async () => {
    if (!organizationId || cancelling) return;
    setCancelling(true);
    setError(null);
    try {
      const response = await fetch(
        resolvePrimaryEnvironmentHttpUrl("/api/amoreg/inspector/schedule", { organizationId }),
        { method: "DELETE", credentials: "include" },
      );
      if (!response.ok) throw new Error("cancel");
      load();
    } catch {
      setError("The scheduled run could not be cancelled. Try again.");
    } finally {
      setCancelling(false);
    }
  };

  const list = runs ?? [];
  const last = list.find((run) => run.status === "completed") ?? null;
  const schedule = profile?.inspectionSchedule ?? null;
  const next = schedule ? nextInspectionRun(schedule, now) : null;
  const upcoming = scheduled
    ? new Date(scheduled.at)
    : profile?.regulatoryMonitoringEnabled && next
      ? next
      : null;
  const openScheduleEditor = () => {
    if (!schedule) return;
    scheduleSession.current += 1;
    setScheduleDraft(schedule);
    setScheduleSaveError(null);
    setScheduleEditorOpen(true);
  };
  const saveSchedule = async () => {
    if (!scheduleDraft || savingSchedule) return;
    const session = scheduleSession.current;
    setSavingSchedule(true);
    setScheduleSaveError(null);
    const failure = await saveProfile({ inspectionSchedule: scheduleDraft });
    if (scheduleSession.current !== session) return; // a newer editor is open
    setSavingSchedule(false);
    if (failure !== null) {
      setScheduleSaveError(failure);
      return;
    }
    setScheduleEditorOpen(false);
  };
  // The local label for a UTC hour, from the next time that hour actually
  // occurs — so a DST-observing zone shows the offset for that date, not a
  // fixed winter one.
  const localHour = (hourUtc: number) =>
    (nextInspectionRun({ cadence: "daily", hourUtc, weekday: 0 }, now) ?? new Date(now)).toLocaleTimeString(
      undefined,
      { hour: "2-digit", minute: "2-digit" },
    );
  const visible = filterInspections(list, filters, query);
  const ordered = sortInspections(visible, sort, now);
  const paged = paginate(ordered, page, pageSize);
  const activeFilters = activeFilterCount(filters);
  const kindCount = (kind: (typeof INSPECTION_KINDS)[number]) =>
    list.filter((run) => inspectionKind(run) === kind).length;
  const statusCount = (status: (typeof INSPECTION_STATUSES)[number]) =>
    list.filter((run) => run.status === status).length;

  return (
    <RegulationPageShell
      title="Inspector"
      description="Review requests and library inspections, with their progress and results."
      actions={
        canManage ? (
          <>
            {/* The workspace's split button: the run on the left, the calendar
                on the right. Manual runs can be requested more than once a day. */}
            <Group aria-label="Inspector run">
              <Button
                type="button"
                size="sm"
                disabled={starting || organizationId === null}
                onClick={() => void runNow()}
                className="cursor-pointer gap-1.5"
              >
                <PlayIcon className="size-3.5" />
                {starting ? "Starting…" : "Inspect library"}
              </Button>
              <GroupSeparator />
              <Menu>
                <MenuTrigger
                  render={
                    <Button
                      aria-label="Schedule options"
                      size="icon-sm"
                      className="cursor-pointer"
                    />
                  }
                >
                  <ChevronDownIcon aria-hidden="true" className="size-4" />
                </MenuTrigger>
                <MenuPopup align="end" className="w-56">
                  <MenuItem
                    onClick={() => {
                      setScheduleAt(toDateTimeLocal(defaultScheduleAt(now)));
                      setScheduleError(null);
                      setScheduleOpen(true);
                    }}
                  >
                    <CalendarPlusIcon className="size-4" />
                    Schedule a run…
                  </MenuItem>
                  <MenuItem disabled={schedule === null} onClick={openScheduleEditor}>
                    <CalendarClockIcon className="size-4" />
                    Change the standing schedule…
                  </MenuItem>
                  {scheduled ? (
                    <>
                      <MenuSeparator />
                      <MenuItem variant="destructive" disabled={cancelling} onClick={() => void cancelScheduled()}>
                        <XIcon className="size-4" />
                        Cancel run · {when(scheduled.at)}
                      </MenuItem>
                    </>
                  ) : null}
                </MenuPopup>
              </Menu>
            </Group>
            <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
              <DialogPopup className="max-w-sm">
                <DialogHeader>
                  <DialogTitle>Schedule a run</DialogTitle>
                  <DialogDescription>
                    Review the whole library at the time you pick. You can run another inspection
                    on the same day.
                  </DialogDescription>
                </DialogHeader>
                <DialogPanel className="space-y-2">
                  <label className="block space-y-1.5">
                    <span className="text-xs font-medium text-muted-foreground">When</span>
                    <Input
                      type="datetime-local"
                      value={scheduleAt}
                      min={toDateTimeLocal(new Date(now))}
                      onChange={(event) => setScheduleAt(event.target.value)}
                    />
                  </label>
                  {scheduleError !== null ? (
                    <p role="alert" className="text-xs text-destructive">
                      {scheduleError}
                    </p>
                  ) : null}
                </DialogPanel>
                <DialogFooter>
                  <DialogClose
                    render={
                      <Button type="button" size="sm" variant="ghost" className="cursor-pointer">
                        Cancel
                      </Button>
                    }
                  />
                  <Button
                    type="button"
                    size="sm"
                    disabled={booking}
                    onClick={() => void scheduleRun()}
                    className="cursor-pointer gap-1.5"
                  >
                    <CalendarPlusIcon className="size-3.5" />
                    {booking ? "Booking…" : "Schedule"}
                  </Button>
                </DialogFooter>
              </DialogPopup>
            </Dialog>
            <Dialog open={scheduleEditorOpen} onOpenChange={setScheduleEditorOpen}>
              <DialogPopup className="max-w-sm" data-testid="inspection-schedule-editor">
                <DialogHeader>
                  <DialogTitle>Standing schedule</DialogTitle>
                  <DialogDescription>
                    How often Inspector sweeps the whole library on its own. Times are UTC; your
                    local time is shown beside each hour.
                  </DialogDescription>
                </DialogHeader>
                <DialogPanel className="space-y-3">
                  {scheduleDraft ? (
                    <>
                      <label className="block space-y-1.5">
                        <span className="text-xs font-medium text-muted-foreground">How often</span>
                        <Select
                          value={scheduleDraft.cadence}
                          onValueChange={(value) =>
                            setScheduleDraft({
                              ...scheduleDraft,
                              cadence: value as InspectionSchedule["cadence"],
                            })
                          }
                        >
                          <SelectTrigger size="sm" className="w-full" aria-label="How often">
                            <span className="truncate">
                              {scheduleDraft.cadence === "off"
                                ? "Off — only when someone asks"
                                : scheduleDraft.cadence === "daily"
                                  ? "Every day"
                                  : "Every week"}
                            </span>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="weekly">Every week</SelectItem>
                            <SelectItem value="daily">Every day</SelectItem>
                            <SelectItem value="off">Off — only when someone asks</SelectItem>
                          </SelectContent>
                        </Select>
                      </label>
                      {scheduleDraft.cadence === "weekly" ? (
                        <label className="block space-y-1.5">
                          <span className="text-xs font-medium text-muted-foreground">On</span>
                          <Select
                            value={String(scheduleDraft.weekday)}
                            onValueChange={(value) =>
                              setScheduleDraft({ ...scheduleDraft, weekday: Number(value) })
                            }
                          >
                            <SelectTrigger size="sm" className="w-full" aria-label="Day of week">
                              <span className="truncate">{WEEKDAY_NAMES[scheduleDraft.weekday]}</span>
                            </SelectTrigger>
                            <SelectContent>
                              {[1, 2, 3, 4, 5, 6, 0].map((day) => (
                                <SelectItem key={day} value={String(day)}>
                                  {WEEKDAY_NAMES[day]}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </label>
                      ) : null}
                      {scheduleDraft.cadence !== "off" ? (
                        <label className="block space-y-1.5">
                          <span className="text-xs font-medium text-muted-foreground">At</span>
                          <Select
                            value={String(scheduleDraft.hourUtc)}
                            onValueChange={(value) =>
                              setScheduleDraft({ ...scheduleDraft, hourUtc: Number(value) })
                            }
                          >
                            <SelectTrigger size="sm" className="w-full" aria-label="Hour">
                              <span className="truncate tabular-nums">
                                {String(scheduleDraft.hourUtc).padStart(2, "0")}:00 UTC ·{" "}
                                {localHour(scheduleDraft.hourUtc)} local
                              </span>
                            </SelectTrigger>
                            <SelectContent>
                              {Array.from({ length: 24 }, (_, hour) => (
                                <SelectItem key={hour} value={String(hour)}>
                                  <span className="tabular-nums">
                                    {String(hour).padStart(2, "0")}:00 UTC · {localHour(hour)} local
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </label>
                      ) : null}
                      <p className="text-xs text-muted-foreground">
                        {scheduleDraft.cadence === "off"
                          ? "Inspector will only run when someone asks, or when a document is uploaded."
                          : profile?.regulatoryMonitoringEnabled
                            ? `${describeInspectionSchedule(scheduleDraft)}. Runs only while regulatory monitoring is on.`
                            : "Regulatory monitoring is off, so scheduled sweeps will not run until it is turned on."}
                      </p>
                      {scheduleSaveError !== null ? (
                        <p role="alert" className="text-xs text-destructive">
                          {scheduleSaveError}
                        </p>
                      ) : null}
                    </>
                  ) : null}
                </DialogPanel>
                <DialogFooter>
                  <DialogClose
                    render={
                      <Button type="button" size="sm" variant="ghost" className="cursor-pointer">
                        Cancel
                      </Button>
                    }
                  />
                  <Button
                    type="button"
                    size="sm"
                    disabled={savingSchedule || scheduleDraft === null}
                    onClick={() => void saveSchedule()}
                    className="cursor-pointer"
                  >
                    {savingSchedule ? "Saving…" : "Save schedule"}
                  </Button>
                </DialogFooter>
              </DialogPopup>
            </Dialog>
          </>
        ) : undefined
      }
    >
      {() => (
        <div className="mt-6 space-y-6 pb-10 text-foreground">
          {error !== null && (actionError !== null || runs !== null) ? (
            <Alert variant="error" controlAlignment="first-line">
              <CircleAlertIcon aria-hidden className="size-4" />
              <AlertTitle>{actionError ? "Inspector request failed" : "Could not refresh Inspector"}</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
              <AlertAction>
                <Button
                  size="xs"
                  variant="outline"
                  onClick={() => {
                    setError(null);
                    load();
                  }}
                >
                  Refresh history
                </Button>
              </AlertAction>
            </Alert>
          ) : null}

          {/* The state of the thing: when it last ran, when it runs next, what
              is booked — beside the calendar of every check it has made. */}
          <div className="grid gap-4 xl:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
            <Card className="isolate h-full overflow-hidden" data-testid="inspection-schedule">
              <PixelField mask="radial-gradient(ellipse 70% 42% at 50% 50%, black 25%, transparent 100%)" />
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(ellipse_55%_35%_at_50%_50%,color-mix(in_oklab,var(--primary)_12%,transparent),transparent)]"
              />
              <CardSpotlight />
              <CardPanel className="flex flex-1 flex-col p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Schedule</p>
                    <p className="mt-1 text-sm font-medium">
                      {!profile || !schedule
                        ? "…"
                        : !profile.regulatoryMonitoringEnabled
                          ? "Off while regulatory monitoring is off"
                          : schedule.cadence === "off"
                            ? "Off"
                            : describeInspectionSchedule(schedule)}
                    </p>
                  </div>
                  {canManage && schedule ? (
                    <Button size="xs" variant="outline" className="shrink-0" onClick={openScheduleEditor}>
                      Change
                    </Button>
                  ) : null}
                </div>
                {/* The wait itself is the card: rolling digits over the pixel field. */}
                <div className="my-auto py-6 text-center" data-testid="inspection-countdown">
                  {upcoming ? (
                    <>
                      <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                        {scheduled ? "Booked run in" : "Next run in"}
                      </p>
                      <Countdown at={upcoming} now={now} />
                      <p className="mt-3 text-xs tabular-nums text-muted-foreground">
                        {when(upcoming.toISOString())}
                        {scheduled ? " · booked" : ""}
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {!profile || !schedule
                        ? "…"
                        : !profile.regulatoryMonitoringEnabled
                          ? "Nothing runs while regulatory monitoring is off."
                          : "No run on the calendar. Run one by hand, or set a schedule."}
                    </p>
                  )}
                </div>
                <div className="flex items-center justify-between gap-3 border-t border-border/60 pt-3 text-xs">
                  <span className="text-muted-foreground">Last completed</span>
                  <span className="min-w-0 font-medium tabular-nums">
                    {last ? (
                      <Link
                        to="/$org/compliance/inspector/$threadId"
                        params={{ org: orgSegment, threadId: last.threadId }}
                        className="underline-offset-4 hover:underline"
                      >
                        {when(last.completedAt ?? last.startedAt)}
                      </Link>
                    ) : runs ? (
                      "Never"
                    ) : (
                      "…"
                    )}
                  </span>
                </div>
              </CardPanel>
            </Card>
            <InspectionActivityCard
              organizationId={organizationId}
              layout="dashboard"
              initialMonths={6}
            />
          </div>

          {organizationId === null && !isLoading ? (
            <ComplianceOrganizationMissing />
          ) : runs === null && error !== null ? (
            <ComplianceUnavailable reason="error" onRetry={load} />
          ) : (
            <div className="flex flex-col gap-4">
              {/* shadcn's data-table toolbar: search, a Filter popover with
                  the kinds and statuses (counts beside each), and a Sort menu. */}
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Input
                  aria-label="Search inspections"
                  placeholder="Search requests and reports…"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  className="w-full sm:w-72"
                />
                <div className="flex items-center gap-2">
                <Popover>
                  <PopoverTrigger
                    render={<Button variant="outline" size="sm" aria-label={`Filter${activeFilters ? `, ${activeFilters} active` : ""}`} />}
                  >
                    <ListFilterIcon aria-hidden />
                    Filter
                    {activeFilters > 0 ? (
                      <Badge variant="secondary" size="sm" className="tabular-nums">
                        {activeFilters}
                      </Badge>
                    ) : null}
                  </PopoverTrigger>
                  <PopoverPopup align="end" className="w-64 p-0" data-testid="inspection-filters">
                    <fieldset className="p-3">
                      <legend className="sr-only">Type</legend>
                      <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Type</p>
                      {INSPECTION_KINDS.map((kind) => (
                        <label key={kind} className="flex cursor-pointer items-center gap-2 rounded-sm px-1 py-1 text-sm hover:bg-accent/60">
                          <Checkbox
                            checked={filters.kinds.includes(kind)}
                            onCheckedChange={(checked) => setFilters((current) => toggleKind(current, kind, checked === true))}
                          />
                          <span className="flex-1">{KIND_LABEL[kind]}</span>
                          <span className="text-xs tabular-nums text-muted-foreground">{runs ? kindCount(kind) : ""}</span>
                        </label>
                      ))}
                    </fieldset>
                    <fieldset className="border-t p-3">
                      <legend className="sr-only">Status</legend>
                      <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Status</p>
                      {INSPECTION_STATUSES.map((status) => (
                        <label key={status} className="flex cursor-pointer items-center gap-2 rounded-sm px-1 py-1 text-sm hover:bg-accent/60">
                          <Checkbox
                            checked={filters.statuses.includes(status)}
                            onCheckedChange={(checked) => setFilters((current) => toggleStatus(current, status, checked === true))}
                          />
                          <span className="flex-1">{STATUS_PILL[status].label}</span>
                          <span className="text-xs tabular-nums text-muted-foreground">{runs ? statusCount(status) : ""}</span>
                        </label>
                      ))}
                    </fieldset>
                    <div className="flex items-center justify-between border-t p-2">
                      <Button variant="ghost" size="xs" disabled={activeFilters === 0} onClick={() => setFilters(NO_FILTERS)}>
                        Clear
                      </Button>
                      <PopoverClose render={<Button variant="outline" size="xs" />}>Done</PopoverClose>
                    </div>
                  </PopoverPopup>
                </Popover>
                <Menu>
                  <MenuTrigger render={<Button variant="outline" size="sm" aria-label={`Sort: ${SORT_LABEL[sort]}`} />}>
                    <ArrowUpDownIcon aria-hidden />
                    {SORT_LABEL[sort]}
                  </MenuTrigger>
                  <MenuPopup align="end" className="w-44">
                    <MenuRadioGroup value={sort} onValueChange={(value) => setSort(value as InspectionSort)}>
                      {INSPECTION_SORTS.map((value) => (
                        <MenuRadioItem key={value} value={value} closeOnClick>
                          {SORT_LABEL[value]}
                        </MenuRadioItem>
                      ))}
                    </MenuRadioGroup>
                  </MenuPopup>
                </Menu>
                </div>
              </div>
              {runs === null ? (
                <ListSkeleton rows={4} />
              ) : visible.length === 0 ? (
                <Empty className="rounded-xl border border-dashed border-border py-10 md:p-10">
                  <EmptyHeader>
                    {list.length ? (
                      <EmptyMedia variant="icon">
                        <InspectorIcon aria-hidden />
                      </EmptyMedia>
                    ) : (
                      <HairlineArt figure={Loupe} className="mb-4 w-48 sm:w-56" />
                    )}
                    <EmptyTitle>
                      {list.length ? "No matching inspections" : "Your reviews start here"}
                    </EmptyTitle>
                    <EmptyDescription>
                      {list.length
                        ? "Try a different filter or search."
                        : "Mention Inspector in a finding’s conversation to request changes, or inspect the whole library."}
                    </EmptyDescription>
                  </EmptyHeader>
                  {list.length ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setFilters(NO_FILTERS);
                        setQuery("");
                      }}
                    >
                      Clear filters
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      render={<Link to="/$org/compliance/findings" params={{ org: orgSegment }} />}
                    >
                      Open findings
                    </Button>
                  )}
                </Empty>
              ) : (
                <>
                  <div className="overflow-hidden rounded-lg border">
                    <Table data-testid="inspection-runs" className="table-fixed">
                      <TableHeader className="bg-muted">
                        <TableRow className="hover:bg-transparent">
                          <TableHead className="w-[24%]">Run</TableHead>
                          <TableHead className="w-32">Type</TableHead>
                          <TableHead>Result</TableHead>
                          <TableHead className="w-36">Recorded</TableHead>
                          <TableHead className="w-28">Status</TableHead>
                          <TableHead className="w-32 text-right">When</TableHead>
                          <TableHead className="w-10">
                            <span className="sr-only">Actions</span>
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paged.items.map((run) => (
                          <InspectionRunRow key={run.threadId} run={run} orgSegment={orgSegment} />
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {/* shadcn's data-table footer: range, rows per page, page nav. */}
                  <div className="flex items-center justify-between gap-4 px-1" data-testid="inspection-runs-pagination">
                    <p className="hidden flex-1 text-sm text-muted-foreground lg:block">
                      Showing {paged.from}–{paged.to} of {ordered.length} {ordered.length === 1 ? "run" : "runs"}
                    </p>
                    <div className="flex w-full items-center gap-6 lg:w-fit lg:gap-8">
                      <label className="hidden items-center gap-2 lg:flex">
                        <span className="text-sm font-medium">Rows per page</span>
                        <Select value={String(pageSize)} onValueChange={(value) => setPageSize(Number(value))}>
                          <SelectTrigger size="sm" className="w-20" aria-label="Rows per page">
                            <span>{pageSize}</span>
                          </SelectTrigger>
                          <SelectContent>
                            {[10, 20, 50].map((size) => (
                              <SelectItem key={size} value={String(size)}>
                                {size}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </label>
                      <p className="text-sm font-medium tabular-nums">
                        Page {paged.page} of {paged.pages}
                      </p>
                      <div className="ml-auto flex items-center gap-2 lg:ml-0">
                        <Button
                          variant="outline"
                          size="icon-sm"
                          className="hidden size-8 lg:inline-flex"
                          aria-label="Go to first page"
                          disabled={paged.page <= 1}
                          onClick={() => setPage(1)}
                        >
                          <ChevronsLeftIcon aria-hidden />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon-sm"
                          className="size-8"
                          aria-label="Go to previous page"
                          disabled={paged.page <= 1}
                          onClick={() => setPage(paged.page - 1)}
                        >
                          <ChevronLeftIcon aria-hidden />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon-sm"
                          className="size-8"
                          aria-label="Go to next page"
                          disabled={paged.page >= paged.pages}
                          onClick={() => setPage(paged.page + 1)}
                        >
                          <ChevronRightIcon aria-hidden />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon-sm"
                          className="hidden size-8 lg:inline-flex"
                          aria-label="Go to last page"
                          disabled={paged.page >= paged.pages}
                          onClick={() => setPage(paged.pages)}
                        >
                          <ChevronsRightIcon aria-hidden />
                        </Button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </RegulationPageShell>
  );
}

export const Route = createFileRoute("/$org/compliance/inspector")({
  component: RegulationInspectorRoute,
});

/** Days · hours · minutes until `at`, each place a rolling digit strip. */
function Countdown({ at, now }: { readonly at: Date; readonly now: number }) {
  const parts = countdownParts(at, now);
  const units = [
    [parts.days, "days"],
    [parts.hours, "hours"],
    [parts.minutes, "min"],
  ] as const;
  const spoken = `${parts.days} days, ${parts.hours} hours and ${parts.minutes} minutes`;
  return (
    <div className="mt-3 flex items-end justify-center gap-6" role="group" aria-label={`Time until the next run: ${spoken}`}>
      {units.map(([value, label]) => (
        <span key={label} className="flex items-end gap-3">
          <span className="flex flex-col items-center">
            <Counter
              value={value}
              places={2}
              fontSize={40}
              padding={8}
              className="font-semibold tracking-tight text-foreground"
              label={`${value} ${label}`}
            />
            <span className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
          </span>
        </span>
      ))}
    </div>
  );
}
